import { useState, useCallback, useRef, useEffect } from 'react';

// Constants for mobile swipe behavior
const SWIPE_THRESHOLD_PERCENTAGE = 0.2; // 20% of screen width
const GESTURE_START_THRESHOLD = 10; // Minimum pixels to start gesture
const ELASTIC_DRAG_LIMIT = -5; // Small elastic feedback when no meeting selected
const ANIMATION_DURATION = 300; // ms
const ANIMATION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

export const useMobileSwipe = (selectedMeeting, selectedMeetingId, onPanelSwitch, disableSwipe = false) => {
  // Panel state
  const [activePanelIndex, setActivePanelIndex] = useState(0); // 0 = MeetingsList, 1 = RightPanel
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Touch tracking refs
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchCurrentRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const translateXRef = useRef(0);
  const containerRef = useRef(null);
  
  // Browser history management refs
  const hasHistoryStateRef = useRef(false);
  const isHandlingBackRef = useRef(false);
  
  // Gesture interruption detection
  const touchTimeoutRef = useRef(null);
  const lastTouchTimeRef = useRef(0);

  // Programmatic panel switching
  const switchToRightPanel = useCallback(() => {
    // Allow switching if we have either full meeting data OR just a meeting ID
    const hasMeetingToShow = selectedMeeting || (selectedMeetingId && selectedMeetingId !== -1);
    if (!hasMeetingToShow || activePanelIndex === 1 || isAnimating) return;
    
    setIsAnimating(true);
    setActivePanelIndex(1);
    
    // Push history state for back button handling
    if (!hasHistoryStateRef.current) {
      window.history.pushState({ mobilePanel: 'right' }, '', window.location.href);
      hasHistoryStateRef.current = true;
    }
    
    if (containerRef.current) {
      containerRef.current.style.transition = `transform ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;
      containerRef.current.style.transform = 'translateX(-100vw)';
      
      setTimeout(() => {
        setIsAnimating(false);
        translateXRef.current = -100;
        onPanelSwitch?.(1);
      }, ANIMATION_DURATION);
    }
  }, [selectedMeeting, selectedMeetingId, activePanelIndex, isAnimating, onPanelSwitch]);

  const switchToLeftPanel = useCallback(() => {
    if (activePanelIndex === 0 || isAnimating) return;
    
    setIsAnimating(true);
    setActivePanelIndex(0);
    
    // Clear history state when going back to meetings list
    if (hasHistoryStateRef.current && !isHandlingBackRef.current) {
      // Go back in history to remove our pushed state
      window.history.back();
    }
    hasHistoryStateRef.current = false;
    
    if (containerRef.current) {
      containerRef.current.style.transition = `transform ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;
      containerRef.current.style.transform = 'translateX(0vw)';
      
      setTimeout(() => {
        setIsAnimating(false);
        translateXRef.current = 0;
        onPanelSwitch?.(0);
        isHandlingBackRef.current = false; // Reset flag after animation
      }, ANIMATION_DURATION);
    }
  }, [activePanelIndex, isAnimating, onPanelSwitch]);

  // Fallback mechanism to ensure panels never get stuck
  const ensureValidPanelPosition = useCallback(() => {
    if (isAnimating) return;
    
    const currentTranslateX = translateXRef.current;
    const tolerance = 5; // Allow small deviations
    
    // Check if we're in an invalid position (not at 0 or -100)
    const isAtLeftPanel = Math.abs(currentTranslateX - 0) < tolerance;
    const isAtRightPanel = Math.abs(currentTranslateX - (-100)) < tolerance;
    
    if (!isAtLeftPanel && !isAtRightPanel) {
      // We're stuck in the middle - snap to nearest valid position
      const midpoint = -50;
      let targetPanelIndex;
      
      if (currentTranslateX > midpoint) {
        targetPanelIndex = 0; // Snap to meetings list
      } else if (selectedMeeting || (selectedMeetingId && selectedMeetingId !== -1)) {
        targetPanelIndex = 1; // Snap to right panel (if meeting selected)
      } else {
        targetPanelIndex = 0; // No meeting selected, must go to meetings list
      }
      
      // Animate to correct position
      setIsAnimating(true);
      setActivePanelIndex(targetPanelIndex);
      
      const targetTranslateX = targetPanelIndex * -100;
      
      if (containerRef.current) {
        containerRef.current.style.transition = `transform ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;
        containerRef.current.style.transform = `translateX(${targetTranslateX}vw)`;
        
        setTimeout(() => {
          setIsAnimating(false);
          translateXRef.current = targetTranslateX;
          onPanelSwitch?.(targetPanelIndex);
        }, ANIMATION_DURATION);
      }
    }
  }, [isAnimating, selectedMeeting, selectedMeetingId, onPanelSwitch]);

  // Touch gesture handlers
  const handleTouchStart = useCallback((e) => {
    if (isAnimating || disableSwipe) return;
    
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
    isDraggingRef.current = false;
    translateXRef.current = activePanelIndex * -100;
    lastTouchTimeRef.current = Date.now();
    
    // Clear any existing timeout
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }
    
    // Set a timeout to detect interrupted gestures
    touchTimeoutRef.current = setTimeout(() => {
      // If no touch end was received, check if we need to fix panel position
      ensureValidPanelPosition();
    }, 500); // 500ms timeout
  }, [isAnimating, activePanelIndex, ensureValidPanelPosition, disableSwipe]);

  const handleTouchMove = useCallback((e) => {
    if (isAnimating || !touchStartRef.current || disableSwipe) return;

    const touch = e.touches[0];
    touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
    lastTouchTimeRef.current = Date.now();
    
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // More aggressive gesture detection to prevent browser interference
    if (!isDraggingRef.current) {
      // Start dragging if:
      // 1. Horizontal movement is dominant AND above threshold, OR
      // 2. Any significant horizontal movement (even with some vertical)
      const hasSignificantHorizontalMovement = absDeltaX > GESTURE_START_THRESHOLD;
      const isHorizontallyDominant = absDeltaX > absDeltaY;
      const isLikelyHorizontalGesture = hasSignificantHorizontalMovement && (isHorizontallyDominant || absDeltaX > absDeltaY * 0.5);
      
      if (isLikelyHorizontalGesture) {
        isDraggingRef.current = true;
        // Aggressively prevent default to stop browser pull-to-refresh
        e.preventDefault();
        if (e.cancelable) {
          e.stopPropagation();
        }
      }
    }
    
    if (isDraggingRef.current) {
      // Prevent all browser default behaviors
      e.preventDefault();
      if (e.cancelable) {
        e.stopPropagation();
      }
      
      // Calculate new translateX based on drag
      const screenWidth = window.innerWidth;
      const dragVw = (deltaX / screenWidth) * 100;
      let newTranslateX = (activePanelIndex * -100) + dragVw;
      
      // Apply boundary constraints based on current panel
      if (activePanelIndex === 0) {
        // On MeetingsList panel
        if (selectedMeeting || (selectedMeetingId && selectedMeetingId !== -1)) {
          // Allow dragging to right panel
          newTranslateX = Math.max(-100, Math.min(0, newTranslateX));
        } else {
          // No meeting selected: only allow elastic feedback
          newTranslateX = Math.max(ELASTIC_DRAG_LIMIT, Math.min(0, newTranslateX));
        }
      } else if (activePanelIndex === 1) {
        // On RightPanel: can only drag back to meetings list
        newTranslateX = Math.max(-100, Math.min(0, newTranslateX));
      }
      
      translateXRef.current = newTranslateX;
      
      // Apply transform for real-time feedback
      if (containerRef.current) {
        containerRef.current.style.transform = `translateX(${newTranslateX}vw)`;
        containerRef.current.style.transition = 'none';
      }
    }
  }, [isAnimating, activePanelIndex, selectedMeeting, selectedMeetingId, disableSwipe]);

  const handleTouchEnd = useCallback(() => {
    // Clear timeout since we received touch end
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }
    
    if (isAnimating || !isDraggingRef.current || disableSwipe) {
      isDraggingRef.current = false;
      return;
    }

    isDraggingRef.current = false;
    const deltaX = touchCurrentRef.current.x - touchStartRef.current.x;
    const threshold = window.innerWidth * SWIPE_THRESHOLD_PERCENTAGE;
    
    // Get current visual position of panels
    const currentTranslateX = translateXRef.current;
    
    let targetPanelIndex = activePanelIndex;
    
    // First, check if swipe meets distance threshold
    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0 && activePanelIndex === 1) {
        // Swipe right: go to meetings list
        targetPanelIndex = 0;
      } else if (deltaX < 0 && activePanelIndex === 0 && (selectedMeeting || (selectedMeetingId && selectedMeetingId !== -1))) {
        // Swipe left: go to right panel (only if meeting selected)
        targetPanelIndex = 1;
      }
    } else {
      // Swipe didn't meet threshold - snap to nearest panel based on current position
      // currentTranslateX ranges from 0 (left panel) to -100 (right panel)
      const midpoint = -50; // Halfway point between panels
      
      if (currentTranslateX > midpoint) {
        // Closer to left panel (meetings list)
        targetPanelIndex = 0;
      } else if (selectedMeeting || (selectedMeetingId && selectedMeetingId !== -1)) {
        // Closer to right panel (and meeting is selected)
        targetPanelIndex = 1;
      } else {
        // No meeting selected, must go back to meetings list
        targetPanelIndex = 0;
      }
    }
    
    // Animate to target panel
    setIsAnimating(true);
    setActivePanelIndex(targetPanelIndex);
    
    const targetTranslateX = targetPanelIndex * -100;
    
    if (containerRef.current) {
      containerRef.current.style.transition = `transform ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;
      containerRef.current.style.transform = `translateX(${targetTranslateX}vw)`;
      
      setTimeout(() => {
        setIsAnimating(false);
        translateXRef.current = targetTranslateX;
        onPanelSwitch?.(targetPanelIndex);
      }, ANIMATION_DURATION);
    }
  }, [isAnimating, activePanelIndex, selectedMeeting, selectedMeetingId, onPanelSwitch, disableSwipe]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (event) => {
      // Only intercept if we're on the right panel and have pushed a history state
      if (activePanelIndex === 1 && hasHistoryStateRef.current) {
        event.preventDefault();
        isHandlingBackRef.current = true;
        
        // Switch to left panel instead of going back in browser history
        setIsAnimating(true);
        setActivePanelIndex(0);
        hasHistoryStateRef.current = false;
        
        if (containerRef.current) {
          containerRef.current.style.transition = `transform ${ANIMATION_DURATION}ms ${ANIMATION_EASING}`;
          containerRef.current.style.transform = 'translateX(0vw)';
          
          setTimeout(() => {
            setIsAnimating(false);
            translateXRef.current = 0;
            onPanelSwitch?.(0);
            isHandlingBackRef.current = false;
          }, ANIMATION_DURATION);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [activePanelIndex, onPanelSwitch]);

  // Initialize history state if starting on right panel (e.g., from direct URL)
  useEffect(() => {
    if (activePanelIndex === 1 && (selectedMeeting || (selectedMeetingId && selectedMeetingId !== -1)) && !hasHistoryStateRef.current) {
      // Push history state if we're starting on the right panel
      window.history.replaceState({ mobilePanel: 'right' }, '', window.location.href);
      hasHistoryStateRef.current = true;
    }
  }, [activePanelIndex, selectedMeeting, selectedMeetingId]);

  // Handle page visibility changes (can interrupt gestures)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible again - check if panels are in valid position
        setTimeout(() => {
          ensureValidPanelPosition();
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [ensureValidPanelPosition]);

  // Clean up history state and timeouts when component unmounts
  useEffect(() => {
    return () => {
      // Clean up any pushed history state on unmount
      if (hasHistoryStateRef.current) {
        hasHistoryStateRef.current = false;
      }
      
      // Clear any pending timeouts
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
        touchTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    // State
    activePanelIndex,
    isAnimating,
    containerRef,
    
    // Actions
    switchToRightPanel,
    switchToLeftPanel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    
    // Additional touch events for better browser compatibility
    handleTouchCancel: handleTouchEnd, // Use same logic as touch end
    
    // Manual state setters (for external control)
    setActivePanelIndex
  };
};